import React, { useState, useEffect } from 'react';
import { Instagram, Twitter, Youtube, Linkedin, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../utils/apiUrl';
import { SEOHead } from '../components/SEOHead';
import './Hosts.css';

export const Hosts = () => {
  const [hosts, setHosts] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch all pages first to find the home page dynamically
      const pagesRes = await axios.get(`${API_URL}/api/cms/pages`);
      const homePage = pagesRes.data.find(p => p.slug === '/' || p.slug === 'home');
      
      if (!homePage) {
        console.error('Home page not found in CMS');
        setLoading(false);
        return;
      }
      
      // Fetch the home page by its dynamic ID to get sections data
      const pageRes = await axios.get(`${API_URL}/api/cms/pages/by-id/${homePage.id}`);
      const hostsSection = pageRes.data.sectionsData.find(s => 
        s.identifier === 'HOSTS_SHOWCASE' || s.sectionType === 'hosts_showcase' || s.title === 'Meet Your Hosts'
      );
      
      // Extract HOST_CARD blocks and transform to host objects
      const hostBlocks = hostsSection?.blocksData?.filter(b => b.blockType === 'HOST_CARD') || [];
      
      const hostsFromBlocks = hostBlocks.map((block, index) => ({
        id: block.id,
        name: block.content.name,
        role: block.content.role,
        bio: block.content.bio,
        headshot: block.content.imageUrl,
        socialLinks: block.content.socialLinks || {},
        isActive: block.content.isActive !== false,
        featuredEpisodes: [],
        order: index
      }));
      
      const episodesRes = await axios.get(`${API_URL}/api/cms/episodes`);
      
      setHosts(hostsFromBlocks);
      setEpisodes(episodesRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hosts-page">
      <SEOHead
        title="Hosts"
        description="Meet the voices behind Golf Nuts — the friends and golfers who make the podcast happen every week."
        path="/hosts"
      />
      <div className="page-header-section">
        <div className="container">
          <h1 className="display-medium">Meet Your Hosts</h1>
          <p className="body-large">The voices behind Golf Nuts</p>
        </div>
      </div>

      <div className="container">
        {hosts.map((host, index) => {
          const hostEpisodes = episodes.filter(ep => 
            host.featuredEpisodes && host.featuredEpisodes.includes(ep.id)
          );
          
          return (
            <section key={host.id} className={`host-section ${index % 2 === 1 ? 'reverse' : ''}`}>
              <div className="host-profile-card">
                <div className="host-image-large">
                  <img src={host.headshot} alt={host.name} />
                </div>
                
                <div className="host-info-content">
                  <h2 className="display-medium">{host.name}</h2>
                  <p className="host-role-large">{host.role}</p>
                  <p className="body-large host-bio">{host.bio}</p>
                  
                  <div className="host-social">
                                    {host.socialLinks?.instagram && (
                                      <a 
                                        href={host.socialLinks.instagram} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="social-btn"
                                      >
                                        <Instagram size={20} />
                                        <span>Instagram</span>
                                      </a>
                                    )}
                                    {host.socialLinks?.twitter && (
                                      <a 
                                        href={host.socialLinks.twitter} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="social-btn"
                                      >
                                        <Twitter size={20} />
                                        <span>X / Twitter</span>
                                      </a>
                                    )}
                                    {host.socialLinks?.youtube && (
                                      <a 
                                        href={host.socialLinks.youtube} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="social-btn"
                                      >
                                        <Youtube size={20} />
                                        <span>YouTube</span>
                                      </a>
                                    )}
                                    {host.socialLinks?.linkedin && (
                                      <a 
                                        href={host.socialLinks.linkedin} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="social-btn"
                                      >
                                        <Linkedin size={20} />
                                        <span>LinkedIn</span>
                                      </a>
                                    )}
                                    {host.socialLinks?.website && (
                                      <a 
                                        href={host.socialLinks.website} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="social-btn"
                                      >
                                        <Globe size={20} />
                                        <span>Website</span>
                                      </a>
                                    )}
                                  </div>
                </div>
              </div>
              
              {/* Featured Episodes - Only show if host has featured episodes */}
              {hostEpisodes.length > 0 && (
                <div className="host-episodes">
                  <h3 className="heading-2">Featured Episodes</h3>
                  <div className="host-episodes-grid">
                    {hostEpisodes.slice(0, 3).map(episode => (
                      <Link to={`/episodes/${episode.slug}`} key={episode.id} className="mini-episode-card hover-lift">
                        <img src={episode.thumbnail} alt={episode.title} />
                        <div className="mini-episode-info">
                          <h4 className="body-medium">{episode.title}</h4>
                          <p className="body-small">{episode.duration}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};
